import { memo } from 'react';
import { Headphones, Mic, Video } from 'lucide-react';
import { cn } from '@cyphex/ui';
import { useDevices } from '../../hooks/useDevices';
import { useMicLevel } from '../../hooks/useMicLevel';
import type { DeviceKind } from '../../media/contract';
import { speakerSelectionSupported } from '../../media/livekitAudio';
import type { DevicePreferences } from '../../media/settings/preferences';
import { DeviceMenu } from '../prejoin/DeviceMenu';

export interface DevicesTabProps {
  devices: DevicePreferences;
  /** Raw microphone capture, for the live level meter. */
  micStream: MediaStream | null;
  onSelect: (kind: DeviceKind, deviceId: string) => void;
}

const SPEAKER_HINT =
  'This browser always plays through the system default output. Change it in your OS sound settings.';

/**
 * Camera / microphone / speaker, switchable mid-call. Selecting a device
 * re-captures and hot-swaps the published track; selecting a speaker calls
 * `setSinkId` on every remote audio element that is playing right now.
 */
export const DevicesTab = memo(function DevicesTab({
  devices, micStream, onSelect,
}: DevicesTabProps) {
  const available = useDevices();
  const level = useMicLevel(micStream);
  const canPickSpeaker = speakerSelectionSupported();

  return (
    <div className="flex flex-col gap-3">
      {available.permissionDenied ? (
        <p className="rounded-lg bg-destructive-subtle px-3 py-2 text-md text-destructive">
          Your browser has not granted access to your devices, so they cannot be listed.
        </p>
      ) : null}

      <DeviceMenu
        label="Camera"
        icon={Video}
        devices={available.cameras}
        selectedId={devices.cameraId ?? undefined}
        onSelect={(id) => onSelect('videoinput', id)}
      />

      <div className="flex flex-col gap-1.5">
        <DeviceMenu
          label="Microphone"
          icon={Mic}
          devices={available.microphones}
          selectedId={devices.micId ?? undefined}
          onSelect={(id) => onSelect('audioinput', id)}
        />
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Input level</span>
          <span className="flex h-1.5 flex-1 overflow-hidden rounded-pill bg-subtle">
            <span
              className={cn('h-full rounded-pill bg-success transition-[width] duration-fast')}
              style={{ width: `${Math.round(level * 100)}%` }}
            />
          </span>
        </div>
      </div>

      <DeviceMenu
        label="Speaker"
        icon={Headphones}
        devices={available.speakers}
        selectedId={devices.speakerId ?? undefined}
        onSelect={(id) => onSelect('audiooutput', id)}
      />
      {canPickSpeaker ? null : (
        <p className="text-sm text-muted-foreground">{SPEAKER_HINT}</p>
      )}
    </div>
  );
});
